import { Check, CheckCircle, CircleNotch, HouseLine, WarningCircle } from "@phosphor-icons/react";
import { type ReactNode, useState } from "react";

import { PasswordInput } from "@/components/ui/password-input";
import {
  type AdminHouseholdDetail,
  type AdminHubCheck,
  getApiErrorMessage,
  useAdminHubCheck,
  useRevealHouseholdSecrets,
  useUpdateHousehold,
} from "@/lib/api";
import { cn } from "@/lib/utils";

import { HaTokenDeleteDialog } from "./ha-token-delete-dialog";
import { InlineEdit } from "./inline-edit";
import { MissingChip } from "./missing-chip";

/** Truncates a long id/url to `keep` characters on each side of an ellipsis. */
function truncateMiddle(value: string, keep = 10): string {
  if (value.length <= keep * 2 + 1) {
    return value;
  }
  return `${value.slice(0, keep)}…${value.slice(-keep)}`;
}

/**
 * What the hub says, not what the record holds.
 *
 * This box used to print a green "Reachable" whenever a URL string was
 * stored, and never called the house once. Beta Test House 14 showed as
 * reachable for a week while every request to it came back 401, and the
 * record was the only thing wrong. A refused token and a silent hub are kept
 * apart because the first is fixed from a desk and the second may be a visit.
 */
function HubBanner({
  url,
  check,
  loading,
  failed,
}: {
  url: string | null;
  check: AdminHubCheck | null | undefined;
  loading: boolean;
  failed: boolean;
}) {
  // A check that never came back is not a hub that answered. Saying nothing
  // for as long as the page is open is how the old badge misled us, so an
  // unfinished check keeps spinning only while it is actually running, and a
  // failed one says out loud that we could not ask.
  const look = loading ? LOOKING : failed || !check ? UNASKED : HUB_LOOK[check.state];
  const Icon = look.icon;
  return (
    <div className={cn("mb-3 flex items-center gap-2.5 rounded-xl p-3", look.tone)}>
      <Icon
        className={cn("size-[18px] shrink-0", look.text, loading && "animate-spin")}
        weight="light"
      />
      <div className="min-w-0">
        <div className={cn("text-sm font-semibold", look.text)}>{look.title}</div>
        {url && (
          <div className="text-muted truncate font-mono text-[11px]">
            {truncateMiddle(url, 16)}
            {check?.version ? ` · ${check.version}` : ""}
          </div>
        )}
      </div>
    </div>
  );
}

const LOOKING = {
  tone: "bg-surface-subtle",
  text: "text-muted",
  icon: CircleNotch,
  title: "Asking the hub\u2026",
} as const;

const UNASKED = {
  tone: "bg-warning/10",
  text: "text-warning",
  icon: WarningCircle,
  title: "Could not ask the hub",
} as const;

const HUB_LOOK = {
  ok: {
    tone: "bg-success/10",
    text: "text-success",
    icon: CheckCircle,
    title: "Reachable, token accepted",
  },
  token_rejected: {
    tone: "bg-destructive/10",
    text: "text-destructive",
    icon: WarningCircle,
    title: "The hub refused this token",
  },
  no_answer: {
    tone: "bg-warning/10",
    text: "text-warning",
    icon: WarningCircle,
    title: "No answer from the hub",
  },
  not_configured: {
    tone: "bg-surface-subtle",
    text: "text-muted",
    icon: HouseLine,
    title: "No hub on record yet",
  },
} as const;

function Row({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="border-border-subtle flex items-center justify-between gap-3.5 border-b py-2.5 text-[13.5px] last:border-b-0">
      <span className="text-muted whitespace-nowrap">{label}</span>
      <span className="text-right font-medium">{value}</span>
    </div>
  );
}

/**
 * Write-only secret field for the HA token. Mirrors `PasswordField` in
 * `preconfig-card.tsx`: shows a masked "set" chip, never the real value, and
 * only calls `onSave` when the admin types a non-empty replacement.
 */
function TokenField({
  hasValue,
  onSave,
  onRemove,
  revealFetcher,
}: {
  hasValue: boolean;
  onSave: (value: string) => Promise<void>;
  onRemove: () => void;
  revealFetcher?: () => Promise<string | null | undefined>;
}) {
  const [draft, setDraft] = useState("");
  // What the eye put in the field. Revealing is not editing, so a draft that
  // still matches this is saved back on blur, which would re-check a hub for
  // nothing and flash a tick as if something had been changed.
  const [revealed, setRevealed] = useState<string | null>(null);
  const [status, setStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [error, setError] = useState("");

  const commit = async () => {
    if (!draft || draft === revealed) {
      return;
    }
    setStatus("saving");
    setError("");
    try {
      await onSave(draft);
      setDraft("");
      setRevealed(null);
      setStatus("saved");
      window.setTimeout(() => setStatus("idle"), 1500);
    } catch (err) {
      setStatus("error");
      setError(getApiErrorMessage(err, "Failed to save."));
    }
  };

  return (
    <div className="border-border-subtle flex flex-col gap-1.5 border-b py-2.5 text-[13.5px] last:border-b-0">
      <div className="flex items-center justify-between gap-3.5">
        <span className="text-muted whitespace-nowrap">Token</span>
        {hasValue ? (
          <span className="flex items-center gap-2">
            <span className="bg-accent/10 text-accent-pressed rounded-sm px-2 py-0.5 text-[11px] font-semibold">
              set ✓
            </span>
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={onRemove}
              className="text-muted hover:text-destructive text-[11px] font-semibold underline-offset-2 hover:underline"
            >
              Remove
            </button>
          </span>
        ) : (
          <MissingChip variant="preship" />
        )}
      </div>
      <div className="flex items-center gap-1.5">
        <PasswordInput
          aria-label="Home Assistant token"
          value={draft}
          revealFetcher={revealFetcher}
          onReveal={(secret) => {
            // Into the field, not beside it: this is the value you select over
            // and paste on top of when you are replacing a token.
            setDraft(secret);
            setRevealed(secret);
          }}
          placeholder={hasValue ? "•••••• (leave blank to keep)" : "Not set"}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => void commit()}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              (e.target as HTMLInputElement).blur();
            }
          }}
          className={cn("h-8 text-[12.5px]", status === "error" && "border-destructive")}
        />
        {status === "saving" && (
          <CircleNotch className="text-muted size-3.5 shrink-0 animate-spin" weight="light" />
        )}
        {status === "saved" && <Check className="text-success size-3.5 shrink-0" weight="light" />}
      </div>
      {status === "error" && error && <p className="text-destructive text-[12px]">{error}</p>}
    </div>
  );
}

interface HomeAssistantCardProps {
  household: AdminHouseholdDetail;
}

/** Rail card summarizing — and letting the admin edit — the household's linked Home Assistant instance. */
export function HomeAssistantCard({ household }: HomeAssistantCardProps) {
  const home = household.home_assistant;
  const updateMutation = useUpdateHousehold();
  // Keyed under the household, so saving a token invalidates this too.
  const hubCheck = useAdminHubCheck(household.id);
  const revealMutation = useRevealHouseholdSecrets();
  const [removingToken, setRemovingToken] = useState(false);

  const saveUrl = (value: string) =>
    updateMutation
      .mutateAsync({ id: household.id, body: { ha_url: value || null } })
      .then(() => undefined);

  const saveToken = (value: string) =>
    updateMutation
      .mutateAsync({ id: household.id, body: { ha_token: value } })
      .then(() => undefined);

  const saveInstanceId = (value: string) =>
    updateMutation
      .mutateAsync({ id: household.id, body: { ha_instance_id: value || null } })
      .then(() => undefined);

  return (
    <div className="bg-card shadow-elevation rounded-md">
      <div className="border-border-subtle flex items-center gap-2.5 border-b px-5 py-[18px]">
        <HouseLine className="text-muted size-[17px]" weight="light" />
        <h2 className="text-primary text-[17px] font-bold">Home Assistant</h2>
      </div>
      <div className="px-5 pt-3.5 pb-4.5">
        {/* Always shown, including for a house with no hub yet: that is the
            state the card is most often opened in before a kit ships, and the
            one the old badge skipped without saying so. */}
        <HubBanner
          url={home.url ?? null}
          check={hubCheck.data}
          loading={hubCheck.isFetching}
          failed={hubCheck.isError}
        />

        <div className="flex flex-col">
          <Row
            label="URL"
            value={
              <span className="inline-flex items-center gap-2">
                {!home.url && <MissingChip variant="preship" />}
                <InlineEdit
                  value={home.url ?? ""}
                  onSave={saveUrl}
                  ariaLabel="Home Assistant URL"
                  placeholder="https://…"
                  className="font-mono text-[12.5px]"
                >
                  {home.url ? (
                    <span className="font-mono text-[12.5px] break-all">{home.url}</span>
                  ) : (
                    <span className="text-faint">—</span>
                  )}
                </InlineEdit>
              </span>
            }
          />
          <Row
            label="Instance ID"
            value={
              <span className="inline-flex items-center gap-2">
                {!home.instance_id && <MissingChip variant="handoff" />}
                <InlineEdit
                  value={home.instance_id ?? ""}
                  onSave={saveInstanceId}
                  ariaLabel="Home Assistant instance id"
                  placeholder="Add instance id"
                  className="font-mono text-[12.5px]"
                >
                  {home.instance_id ? (
                    <span className="font-mono text-[12.5px] break-all">
                      {truncateMiddle(home.instance_id)}
                    </span>
                  ) : (
                    <span className="text-faint">—</span>
                  )}
                </InlineEdit>
              </span>
            }
          />
          {home.version && <Row label="Version" value={home.version} />}
          {home.installation_type && <Row label="Install" value={home.installation_type} />}
          {home.hardware && <Row label="Hardware" value={home.hardware} />}
          {home.remote_access && <Row label="Remote" value={home.remote_access} />}
        </div>

        <TokenField
          // Remounted when the stored token comes or goes, which clears the
          // draft and what the eye revealed. Without it, removing a token
          // leaves the old secret sitting in the field, and saving that very
          // token back is refused as "nothing changed".
          key={home.has_token ? "token-set" : "token-unset"}
          hasValue={!!home.has_token}
          onSave={saveToken}
          onRemove={() => setRemovingToken(true)}
          revealFetcher={() =>
            revealMutation.mutateAsync({ id: household.id }).then((secrets) => secrets?.ha_token)
          }
        />

        {home.integrations && home.integrations.length > 0 && (
          <>
            <div className="text-muted mt-3 mb-1.5 text-xs">Integrations</div>
            <div className="flex flex-wrap gap-1.5">
              {home.integrations.map((name) => (
                <span
                  key={name}
                  className="bg-surface-subtle text-primary rounded-sm px-2.5 py-1 text-xs font-medium"
                >
                  {name}
                </span>
              ))}
            </div>
          </>
        )}

        {home.addons && home.addons.length > 0 && (
          <>
            <div className="text-muted mt-3 mb-1.5 text-xs">Add-ons</div>
            <div className="flex flex-wrap gap-1.5">
              {home.addons.map((name) => (
                <span
                  key={name}
                  className="bg-surface-subtle text-primary rounded-sm px-2.5 py-1 text-xs font-medium"
                >
                  {name}
                </span>
              ))}
            </div>
          </>
        )}
      </div>
      <HaTokenDeleteDialog
        householdId={household.id}
        open={removingToken}
        onOpenChange={setRemovingToken}
      />
    </div>
  );
}
